import useStripeAccount from '@hooks/useStripeAccount';
import useStripeConnect from '@hooks/useStripeConnect';
import { Box, CircularProgress, DialogContent, Typography } from '@mui/material';
import {
    ConnectAccountManagement,
    ConnectAccountOnboarding,
    ConnectComponentsProvider,
} from '@stripe/react-connect-js';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { EventForm } from '../eventCreation/CreateEventUtils';
import ManagePaymentAdmin from '@atoms/ManagePaymentAdmin';
import { TeamForm } from '@components/teamCreation/CreateTeamUtils';

interface Props {
    connectedAccountId: string;
}

export default function StripeLinkStep({ connectedAccountId }: Props) {
    const { watch } = useFormContext<EventForm | TeamForm>();
    const [stripeDetailsSubmitted] = watch(['stripeDetailsSubmitted']);

    const path = window.location.pathname;
    const onTeam =
        path.split('teams/')[1]?.length >= 31 && path.split('teams/')[1].split('-')?.length === 5;
    const onEvent =
        path.split('events/')[1]?.length >= 31 && path.split('events/')[1].split('-')?.length === 5;
    const editingDetails = onTeam || onEvent;

    const { stripeInfo, loading, refreshStripeInfo, error } = useStripeAccount();
    const [onboardingExited, setOnboardingExited] = useState(false);
    const stripeConnectInstance = useStripeConnect(connectedAccountId);

    // console.log(
    //     `stripe details submitted: value: ${stripeInfo?.detailsSubmitted} form: ${stripeDetailsSubmitted}`,
    // );

    console.log('stripeInfo', {
        stripeInfo,
        stripeDetailsSubmitted,
        onboardingExited,
        stripeConnectInstance: !!stripeConnectInstance,
    });

    if (loading) {
        return (
            <>
                <DialogContent>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        margin="4rem"
                    >
                        <CircularProgress size="3rem" />
                    </Box>
                </DialogContent>
            </>
        );
    }

    const showPayToPreview = onboardingExited || stripeDetailsSubmitted;

    return (
        <>
            <DialogContent>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    margin="1rem"
                    sx={{ minHeight: '15rem' }}
                >
                    {showPayToPreview ? <ManagePaymentAdmin /> : null}
                    {(() => {
                        if (!stripeInfo?.id) {
                            // There was an error initiating the stripe account, we should retry or add a different email
                            return (
                                <>
                                    <Typography variant="h5">Error initiating account</Typography>
                                    <Typography m={1}>We are monitoring the situation.</Typography>
                                    <Typography>
                                        Try refreshing the page and coming back to this page.
                                    </Typography>
                                </>
                            );
                        }
                        // if stripe details have been submitted, show success & allow adding another admin
                        if (stripeDetailsSubmitted) {
                            if (editingDetails) {
                                return (
                                    <>
                                        {stripeConnectInstance ? (
                                            <ConnectComponentsProvider
                                                connectInstance={stripeConnectInstance}
                                            >
                                                <ConnectAccountManagement
                                                // Optional:
                                                // collectionOptions={{
                                                //   fields: 'eventually_due',
                                                //   futureRequirements: 'include',
                                                // }}
                                                />
                                            </ConnectComponentsProvider>
                                        ) : (
                                            <p>Stripe instance loading...</p>
                                        )}
                                    </>
                                );
                            } else {
                                // don't show payment editing config if the event hasn't been published (when we're in the creation modal)
                                return null;
                            }
                        }

                        // FIXME: verify noticeable bug***********
                        // if (onboardingExited) {
                        //     // if user exited onboarding, show status, error & retry option
                        //     // on first account setup, we get stuck on this screen, instead, we should refresh their stripe info and show account management
                        //     // Reproduce:
                        //     // 1. DELETE your stripe account via the command line (see curl command in backend repo)
                        //     // 2. DELETE the whole row for your user in the stripe table
                        //     // 3. create an event/team and link your stripe account
                        //     // 3.1: I was creating as a NON PROFIT! (this may be the issue)
                        //     // 4. you will get stuck with this state
                        //     // the app should have refreshed the stripe info, noticed that you are now setup (stripeDetailsSubmitted) and shown the account management screen above...
                        //     return (
                        //         <>
                        //             <p>Stripe Onboarding has exited</p>
                        //         </>
                        //     );
                        // }

                        // if stripe account is not set up, show onboarding
                        return (
                            <>
                                {stripeInfo?.id &&
                                    !stripeConnectInstance && ( // FIXME: do we need this?
                                        <h2>Add information to start accepting money</h2>
                                    )}
                                {stripeConnectInstance && (
                                    <ConnectComponentsProvider
                                        connectInstance={stripeConnectInstance}
                                    >
                                        <ConnectAccountOnboarding
                                            onExit={() => {
                                                refreshStripeInfo();
                                                setOnboardingExited(true);
                                            }}
                                            collectionOptions={{
                                                fields: 'eventually_due',
                                                futureRequirements: 'omit',
                                            }}
                                        />
                                    </ConnectComponentsProvider>
                                )}
                                {error && <p className="error">Something went wrong!</p>}
                            </>
                        );
                    })()}
                </Box>
            </DialogContent>
        </>
    );
}
